import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import type { AccountCashesBalancesReportFindInterface } from '@zaoblako/shared/modules/dashboard/dtos/acb/account-cashes-balances-report-find.dto';
import type { AvailableWidgetTreeInterface } from '@zaoblako/shared/modules/dashboard/dtos/available.widget.tree.dto';
import type { DashboardTemplateFindInterface } from '@zaoblako/shared/modules/dashboard/dtos/dashboard-template/dashboard-template-find.dto';
import type { DashboardTemplateShareWithInterface } from '@zaoblako/shared/modules/dashboard/dtos/dashboard-template/dashboard-template-share-with.dto';
import { EventCalendarReportQueryDto } from '@zaoblako/shared/modules/dashboard/dtos/event-calendar/event.calendar.report.query.dto';
import type { HelpdeskLastTasksWidgetResultFindInterface } from '@zaoblako/shared/modules/dashboard/dtos/helpdesk/last-tasks/helpdesk-last-tasks-widget-result-find.dto';
import type { HelpdeskNewTasksWidgetResultFindInterface } from '@zaoblako/shared/modules/dashboard/dtos/helpdesk/new-tasks/helpdesk-new-tasks-widget-result-find.dto';
import type { HelpdeskOpenedTasksWidgetResultFindInterface } from '@zaoblako/shared/modules/dashboard/dtos/helpdesk/open-tasks/helpdesk-opened-tasks-widget-result-find.dto';
import { HelpdeskOpenedTasksWidgetQueryDto } from '@zaoblako/shared/modules/dashboard/dtos/helpdesk/open-tasks/helpdesk.opened.tasks.widget.query.dto';
import { HelpdeskStatsWidgetQueryDto } from '@zaoblako/shared/modules/dashboard/dtos/helpdesk/stats/helpdesk.stats.widget.query.dto';
import { HelpdeskStatsWidgetResultDto } from '@zaoblako/shared/modules/dashboard/dtos/helpdesk/stats/helpdesk.stats.widget.result.dto';
import { OverspendingWidgetItemInterface } from '@zaoblako/shared/modules/dashboard/dtos/overspending-widget/overspending-widget-item.dto';
import { OverspendingWidgetQueryInterface } from '@zaoblako/shared/modules/dashboard/dtos/overspending-widget/overspending-widget-query.dto';
import { ProfitAndLossDirectionReport } from '@zaoblako/shared/modules/dashboard/dtos/pnl/profit.and.loss.direction.report';
import { ProfitAndLossReport } from '@zaoblako/shared/modules/dashboard/dtos/pnl/profit.and.loss.report';
import { ProjectActionsWidgetQueryDto } from '@zaoblako/shared/modules/dashboard/dtos/project-actions/project.actions.widget.query.dto';
import { ProjectActionsWidgetResultDto } from '@zaoblako/shared/modules/dashboard/dtos/project-actions/project.actions.widget.result.dto';
import { SavedFilterReportQueryDto } from '@zaoblako/shared/modules/dashboard/dtos/saved.filter.report.query.dto';
import type { DashboardSettingsItemFindInterface } from '@zaoblako/shared/modules/dashboard/dtos/settings/dashboard.settings.item.find.dto';
import type { UnallocatedDocumentsFindInterface } from '@zaoblako/shared/modules/dashboard/dtos/unallocated-documents/unallocated-documents-find.dto';
import type { UserBoardTabInterface } from '@zaoblako/shared/modules/dashboard/dtos/user-board-tab.dto';
import { VatAccountsReportDto } from '@zaoblako/shared/modules/dashboard/dtos/vat-accounts-report/vat.accounts.report.dto';
import { VatAccountsReportQueryDto } from '@zaoblako/shared/modules/dashboard/dtos/vat-accounts-report/vat.accounts.report.query.dto';
import type { WidgetBaseQueryInterface } from '@zaoblako/shared/modules/dashboard/dtos/widget/widget-base-query.dto';
import { DashboardWidgetTypeEnum } from '@zaoblako/shared/modules/dashboard/enums/dashboard.widget.type.enum';
import type { CreditDebitReportFindInterface } from '@zaoblako/shared/modules/debts/dtos/credit-debit-report/credit-debit-report-find.dto';
import type { CreditDebitReportQueryInterface } from '@zaoblako/shared/modules/debts/dtos/credit-debit-report/credit-debit-report-query.dto';
import { AbcAnalysisRowInterface } from '@zaoblako/shared/modules/document/dtos/abc-analysis/abc-analysis-row.dto';
import { AbcAnalysisWidgetQueryInterface } from '@zaoblako/shared/modules/document/dtos/abc-analysis/abc-analysis-widget-query.dto';
import type { CashFlowAnalysisFindInterface } from '@zaoblako/shared/modules/document/dtos/cash-flow-analysis/cash-flow-analysis-find.dto';
import type { CashFlowAnalysisQueryInterface } from '@zaoblako/shared/modules/document/dtos/cash-flow-analysis/cash-flow-analysis-query.dto';
import { CreditDebitReportTypeEnum } from '@zaoblako/shared/modules/document/enums/credit.debit.report.type.enum';
import type { BalanceSheetWidgetQueryInterface } from '@zaoblako/shared/modules/finance/dtos/balance-sheet-widget/balance-sheet-widget-query.dto';
import type { BalanceSheetWidgetInterface } from '@zaoblako/shared/modules/finance/dtos/balance-sheet-widget/balance-sheet-widget.dto';
import type { HelpdeskLastTasksQueryInterface } from '@zaoblako/shared/modules/helpdesk/dto/widgets/helpdesk.last.tasks.query.dto';
import type { EventCalendarWidgetResultFindInterface } from '@zaoblako/shared/modules/report/dtos/event-calendar/event-calendar-widget-result-find.dto';
import { BaseService } from '@zaoblako/web/core/services/base.service';
import { DirectionService } from '@zaoblako/web/core/services/finance/direction.service';
import { map, Observable } from 'rxjs';
import { DashboardAccountsDebt } from '../../../modules/dashboard/models/dashboard-accounts-debt.interface';
import { DashboardUtilsService } from './dashboard-utils.service';

@Injectable()
export class DashboardService extends BaseService {
  protected baseUrl = '/basis/board/dashboard';
  private widgetInfoUrl = '/dashboard';
  private helpdeskWidgetsBaseUrl = '/helpdesk/widget';

  public constructor(
    private readonly dashboardUtilsService: DashboardUtilsService,
    private readonly directionService: DirectionService
  ) {
    super();
  }

  public getWidgetsList(): Observable<AvailableWidgetTreeInterface[]> {
    return this.http.get<AvailableWidgetTreeInterface[]>('/basis/board/available-widget-types');
  }

  public getUserBoard(): Observable<UserBoardTabInterface[]> {
    return this.http.get<UserBoardTabInterface[]>('/basis/board/user');
  }

  public toTemplate(
    dashboardId: number,
    body: DashboardTemplateShareWithInterface
  ): Observable<DashboardTemplateFindInterface> {
    return this.http.post<DashboardTemplateFindInterface>(`${this.baseUrl}/${dashboardId}/to-template`, body);
  }

  public selectDashboard(dashboardId: number): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${dashboardId}/select`, {});
  }

  public savedFilterReport(queryParams: SavedFilterReportQueryDto): Observable<any> {
    return this.http.get<any>('/basis/dashboard/saved-filter-report', { params: this.objectToHttpParams(queryParams) });
  }

  public getBalances(
    widget: DashboardSettingsItemFindInterface,
    forceUpdate = false
  ): Observable<AccountCashesBalancesReportFindInterface> {
    let params = this.widgetDataToHttpParams(widget);
    if (forceUpdate) {
      params = params.set('forceUpdate', true);
    }
    return this.http.get<AccountCashesBalancesReportFindInterface>(
      `${this.widgetInfoUrl}/balances/accounts-and-cashes`,
      {
        params: params
      }
    );
  }

  public getProfitAndLoss(widget: DashboardSettingsItemFindInterface): Observable<ProfitAndLossReport> {
    return this.http.get<ProfitAndLossReport>(`${this.widgetInfoUrl}/pnl-report/all`, {
      params: this.widgetDataToHttpParams(widget)
    });
  }

  public getAccountsWidget(
    widget: DashboardSettingsItemFindInterface,
    forceUpdate = false
  ): Observable<DashboardAccountsDebt> {
    let params = this.widgetDataToHttpParams(widget);

    if (
      [
        DashboardWidgetTypeEnum.DEBTORS,
        DashboardWidgetTypeEnum.ACCOUNTS_RECEIVABLE,
        DashboardWidgetTypeEnum.ACCOUNTS_PAYABLE
      ].includes(widget.widgetType) &&
      forceUpdate
    ) {
      params = params.set('forceUpdate', true);
    }

    let request: Observable<CreditDebitReportFindInterface>;
    if (widget.widgetType === DashboardWidgetTypeEnum.ACCOUNTS_RECEIVABLE) {
      request = this.getAccountsReceivable(params, false);
    } else if (widget.widgetType === DashboardWidgetTypeEnum.ACCOUNTS_PAYABLE) {
      request = this.getAccountsPayable(params, false);
    } else if (widget.widgetType === DashboardWidgetTypeEnum.DEBTORS) {
      request = this.getDebtors(params, false);
    }

    return request.pipe(
      map((data) => ({
        ...data,
        items: this.dashboardUtilsService.getAccountsDebtWidgetData(data.items)
      }))
    );
  }

  public getAccountsReceivable(
    queryParams?: CreditDebitReportQueryInterface | HttpParams,
    toHttpParams = true
  ): Observable<CreditDebitReportFindInterface> {
    return this.http.get<CreditDebitReportFindInterface>(
      `${this.widgetInfoUrl}/accounts-report/` + CreditDebitReportTypeEnum.DEBIT,
      {
        params: (toHttpParams ? this.objectToHttpParams(queryParams) : queryParams) as any
      }
    );
  }

  public getAccountsPayable(
    queryParams?: CreditDebitReportQueryInterface | HttpParams,
    toHttpParams = true
  ): Observable<CreditDebitReportFindInterface> {
    return this.http.get<CreditDebitReportFindInterface>(
      `${this.widgetInfoUrl}/accounts-report/` + CreditDebitReportTypeEnum.CREDIT,
      {
        params: (toHttpParams ? this.objectToHttpParams(queryParams) : queryParams) as any
      }
    );
  }

  public getDebtors(
    queryParams?: CreditDebitReportQueryInterface | HttpParams,
    toHttpParams = true
  ): Observable<CreditDebitReportFindInterface> {
    return this.http.get<CreditDebitReportFindInterface>(
      `${this.widgetInfoUrl}/accounts-report/` + CreditDebitReportTypeEnum.DEBTORS,
      {
        params: (toHttpParams ? this.objectToHttpParams(queryParams) : queryParams) as any
      }
    );
  }

  public getUnallocatedDocuments(
    widget: DashboardSettingsItemFindInterface
  ): Observable<UnallocatedDocumentsFindInterface> {
    return this.http.get<UnallocatedDocumentsFindInterface>(`${this.widgetInfoUrl}/unallocated-documents`, {
      params: this.widgetDataToHttpParams(widget)
    });
  }

  public getDirectionBudget(widget: DashboardSettingsItemFindInterface): Observable<ProfitAndLossDirectionReport[]> {
    return this.http.get<ProfitAndLossDirectionReport[]>(`${this.widgetInfoUrl}/pnl-report/concretes`, {
      params: this.widgetDataToHttpParams(widget)
    });
  }

  public getEventCalendar(
    queryParams: EventCalendarReportQueryDto
  ): Observable<EventCalendarWidgetResultFindInterface> {
    return this.http.get<EventCalendarWidgetResultFindInterface>(`${this.widgetInfoUrl}/event-calendar`, {
      params: this.objectToHttpParams(queryParams) as any
    });
  }

  public getVatAccounts(queryParams: VatAccountsReportQueryDto): Observable<VatAccountsReportDto> {
    return this.http.get<VatAccountsReportDto>(`${this.widgetInfoUrl}/vat-accounts`, { params: queryParams as any });
  }

  public getProjectActions(queryParams: ProjectActionsWidgetQueryDto): Observable<ProjectActionsWidgetResultDto> {
    return this.http.get<ProjectActionsWidgetResultDto>(`${this.widgetInfoUrl}/project-actions`, {
      params: this.objectToHttpParams(queryParams) as any
    });
  }

  public getHelpdeskOpenedTasks(
    queryParams: HelpdeskOpenedTasksWidgetQueryDto
  ): Observable<HelpdeskOpenedTasksWidgetResultFindInterface> {
    return this.http.get<HelpdeskOpenedTasksWidgetResultFindInterface>(`${this.helpdeskWidgetsBaseUrl}/opened-tasks`, {
      params: this.objectToHttpParams(queryParams) as any
    });
  }

  public getHelpdeskNewTasks(
    queryParams?: HelpdeskOpenedTasksWidgetQueryDto
  ): Observable<HelpdeskNewTasksWidgetResultFindInterface> {
    return this.http.get<HelpdeskNewTasksWidgetResultFindInterface>(`${this.helpdeskWidgetsBaseUrl}/new-tasks`, {
      params: this.objectToHttpParams(queryParams) as any
    });
  }

  public getHelpdeskStats(queryParams: HelpdeskStatsWidgetQueryDto): Observable<HelpdeskStatsWidgetResultDto> {
    return this.http.get<HelpdeskStatsWidgetResultDto>(`${this.helpdeskWidgetsBaseUrl}/stats`, {
      params: this.objectToHttpParams(queryParams) as any
    });
  }

  public getHelpDeskLastTasks(
    queryParams: HelpdeskLastTasksQueryInterface
  ): Observable<HelpdeskLastTasksWidgetResultFindInterface> {
    return this.http.get<HelpdeskLastTasksWidgetResultFindInterface>(`${this.helpdeskWidgetsBaseUrl}/last-tasks`, {
      params: this.objectToHttpParams(queryParams) as any
    });
  }

  public getCashFlow(queryParams: CashFlowAnalysisQueryInterface): Observable<CashFlowAnalysisFindInterface> {
    return this.http.get<CashFlowAnalysisFindInterface>('/finance/document/cash-flow', {
      params: this.objectToHttpParams(queryParams) as any
    });
  }

  public getAbcAnalysis(queryParams: AbcAnalysisWidgetQueryInterface): Observable<AbcAnalysisRowInterface[]> {
    return this.http.get<AbcAnalysisRowInterface[]>('/finance/document/abc-analysis/widget', {
      params: this.objectToHttpParams(queryParams as any)
    });
  }

  public getOverspending(queryParams: OverspendingWidgetQueryInterface): Observable<OverspendingWidgetItemInterface[]> {
    return this.http.get<OverspendingWidgetItemInterface[]>('/basis/dashboard/overspending', {
      params: this.objectToHttpParams(queryParams) as any
    });
  }

  public getBalanceSheet(queryParams: BalanceSheetWidgetQueryInterface): Observable<BalanceSheetWidgetInterface> {
    return this.http.get<BalanceSheetWidgetInterface>('/finance/plan-fact/report/balance-sheet/widget', {
      params: this.objectToHttpParams(queryParams) as any
    });
  }

  public widgetDataToHttpParams(widget: DashboardSettingsItemFindInterface): HttpParams {
    const params: WidgetBaseQueryInterface = {
      directionIds: this.directionService.getRootDirections(widget.directions)?.map((direction) => direction.id),
      unitIds: widget.units?.map((unit) => unit.id),
      companyIds: widget.companies?.map((company) => company.id),
      bankDetailsIds: widget.bankDetails?.map((bankDetail) => bankDetail.id),
      method: widget.method,
      period: widget.calendarPeriod,
      shift: widget.periodShift
    };

    if (widget.widgetType === DashboardWidgetTypeEnum.DEBTORS) {
      params['onlyPositive'] = widget.onlyPositive;
    }
    if (widget.widgetType === DashboardWidgetTypeEnum.UNALLOCATED_DOCUMENTS) {
      params['methods'] = widget.methods;
      delete params.method;
    }
    if (widget.widgetType === DashboardWidgetTypeEnum.ACCOUNTS_MONEY) {
      params['sort'] = widget['sortField'];
    }
    return this.objectToHttpParams(params);
  }
}
